import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { rewriteR2Url } from '../../utils/r2Url';

const BASE_URL = 'https://tradermap.io';
const SUPPORTED_LANGUAGES = ['en', 'ru', 'ko', 'zh'];

/**
 * SEO Component - Manages document head for SEO
 *
 * Features:
 * - Dynamic title and description per page/language
 * - Canonical URL
 * - hreflang tags for all language versions
 * - OpenGraph tags
 * - JSON-LD structured data (dynamic for coin pages and articles)
 * - noindex support for 404/private pages
 *
 * @param {string} page - Page type (trades, coinDetails, insights, insightDetail, etc.)
 * @param {boolean} noindex - Whether to add noindex meta tag
 * @param {object} dynamicParams - Dynamic parameters for title/description
 * @param {boolean} skipTitle - Skip updating document title
 * @param {object} coinData - Coin-specific data for JSON-LD (price, marketCap, etc.)
 * @param {string} title - Custom title (overrides translation)
 * @param {string} description - Custom description (overrides translation)
 * @param {string} image - Custom OG image URL
 * @param {string} keywords - Custom keywords meta tag
 * @param {object} articleData - Article data for JSON-LD (title, excerpt, coverImage, authorName, publishedAt, updatedAt)
 */
export default function SEO({
  page = 'trades',
  noindex = false,
  dynamicParams = {},
  skipTitle = false,
  coinData = null,
  title: customTitle = null,
  description: customDescription = null,
  image: customImage = null,
  keywords = null,
  articleData = null
}) {
  const { t } = useTranslation();
  const { language } = useLanguage();
  const location = useLocation();

  useEffect(() => {
    // Get translated title and description with dynamic params (or use custom values)
    const title = customTitle || t(`seo.${page}.title`, {
      defaultValue: 'TraderMap',
      ...dynamicParams
    });
    const description = customDescription || t(`seo.${page}.description`, {
      defaultValue: 'Real-time crypto whale tracking platform',
      ...dynamicParams
    });

    // Update document title (skip if page handles it dynamically, e.g., BTC price)
    if (!skipTitle) {
      document.title = title;
    }

    // Helper to create or update meta tag
    const setMetaTag = (name, content, property = false) => {
      const attr = property ? 'property' : 'name';
      let meta = document.querySelector(`meta[${attr}="${name}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attr, name);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };

    // Helper to create or update link tag
    const setLinkTag = (rel, href, hreflang = null) => {
      const selector = hreflang
        ? `link[rel="${rel}"][hreflang="${hreflang}"]`
        : `link[rel="${rel}"]:not([hreflang])`;

      let link = document.querySelector(selector);
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', rel);
        if (hreflang) link.setAttribute('hreflang', hreflang);
        document.head.appendChild(link);
      }
      link.setAttribute('href', href);
    };

    // Build canonical URL
    const getPagePath = () => {
      switch (page) {
        case 'landing': return '/';
        case 'trades': return '/trades';
        case 'hyperliquid': return '/hyperliquid';
        case 'liquidationMap': return '/liquidation-map';
        case 'polymarket': return '/polymarket';
        case 'rsi': return '/rsi';
        case 'bubbles': return '/bubbles';
        case 'etfFlows': return '/etf-flows';
        case 'calculator': return '/calculator';
        case 'calculatorCoin': return `/calculator/${dynamicParams.coinSymbol || 'BTC'}`;
        case 'coinDetails': return `/price/${dynamicParams.coinSymbol || 'BTC'}`;
        case 'privacy': return '/privacy';
        case 'terms': return '/terms';
        case 'disclaimer': return '/disclaimer';
        case 'terminal': return '/terminal';
        case 'aiTrader': return '/ai-trader';
        case 'insights': return '/insights';
        case 'insightCategory': return `/insights/${dynamicParams.slug || ''}`;
        case 'insightDetail': return `/insights/${dynamicParams.slug || ''}`;
        default: return '/trades';
      }
    };

    const pagePath = getPagePath();
    const canonicalUrl = language === 'en'
      ? `${BASE_URL}${pagePath}`
      : `${BASE_URL}/${language}${pagePath}`;

    // Set canonical
    setLinkTag('canonical', canonicalUrl);

    // Set meta description
    setMetaTag('description', description);

    // Set keywords meta tag if provided
    if (keywords) {
      setMetaTag('keywords', keywords);
    }

    // Set robots meta tag for noindex pages (404, etc.)
    if (noindex) {
      setMetaTag('robots', 'noindex, nofollow');
    } else {
      // Remove noindex if it was previously set
      const robotsMeta = document.querySelector('meta[name="robots"]');
      if (robotsMeta) robotsMeta.remove();
    }

    // Select OG image based on page type or use custom image
    let ogImage;
    if (customImage) {
      ogImage = customImage;
    } else if (page === 'coinDetails' || page === 'calculatorCoin') {
      ogImage = `${BASE_URL}/og-coins.png`;
    } else {
      ogImage = `${BASE_URL}/og-image.png`;
    }

    // Set OpenGraph tags
    setMetaTag('og:title', title, true);
    setMetaTag('og:description', description, true);
    setMetaTag('og:url', canonicalUrl, true);
    setMetaTag('og:type', articleData ? 'article' : 'website', true);
    setMetaTag('og:site_name', 'TraderMap', true);
    setMetaTag('og:image', ogImage, true);

    // Set article-specific OpenGraph tags
    if (articleData) {
      if (articleData.publishedAt) {
        setMetaTag('article:published_time', new Date(articleData.publishedAt).toISOString(), true);
      }
      if (articleData.updatedAt) {
        setMetaTag('article:modified_time', new Date(articleData.updatedAt).toISOString(), true);
      }
      if (articleData.authorName) {
        setMetaTag('article:author', articleData.authorName, true);
      }
    }

    // Set Twitter Card tags
    setMetaTag('twitter:card', 'summary_large_image');
    setMetaTag('twitter:title', title);
    setMetaTag('twitter:description', description);
    setMetaTag('twitter:image', ogImage);

    // Set hreflang tags for all languages
    SUPPORTED_LANGUAGES.forEach(lang => {
      const langUrl = lang === 'en'
        ? `${BASE_URL}${pagePath}`
        : `${BASE_URL}/${lang}${pagePath}`;
      setLinkTag('alternate', langUrl, lang);
    });

    // Set x-default hreflang (points to English version)
    setLinkTag('alternate', `${BASE_URL}${pagePath}`, 'x-default');

    // Add JSON-LD structured data (skip for coin pages - handled server-side)
    if (page !== 'coinDetails') {
      let jsonLdScript = document.querySelector('script[type="application/ld+json"]');
      if (!jsonLdScript) {
        jsonLdScript = document.createElement('script');
        jsonLdScript.setAttribute('type', 'application/ld+json');
        document.head.appendChild(jsonLdScript);
      }

      let jsonLd;

      // Article JSON-LD for blog posts
      if (articleData) {
        // Use Organization type for author profiles with avatar, Person for free text
        const authorJsonLd = articleData.author && articleData.author.avatar
          ? {
              "@type": "Organization",
              "name": articleData.author.name,
              "url": "https://tradermap.io",
              "logo": { "@type": "ImageObject", "url": rewriteR2Url(articleData.author.avatar) }
            }
          : {
              "@type": "Person",
              "name": (articleData.author && articleData.author.name) || articleData.authorName || "TraderMap Team"
            };

        jsonLd = {
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": articleData.title,
          "description": articleData.excerpt || description,
          "image": rewriteR2Url(articleData.coverImage) || ogImage,
          "author": authorJsonLd,
          "publisher": {
            "@type": "Organization",
            "name": "TraderMap",
            "logo": {
              "@type": "ImageObject",
              "url": `${BASE_URL}/logo.png`
            }
          },
          "url": canonicalUrl,
          "mainEntityOfPage": {
            "@type": "WebPage",
            "@id": canonicalUrl
          }
        };

        if (articleData.publishedAt) {
          jsonLd.datePublished = new Date(articleData.publishedAt).toISOString();
        }
        if (articleData.updatedAt) {
          jsonLd.dateModified = new Date(articleData.updatedAt).toISOString();
        }
      } else {
        // Default JSON-LD for non-coin, non-article pages
        jsonLd = {
          "@context": "https://schema.org",
          "@type": "WebApplication",
          "name": "TraderMap",
          "url": BASE_URL,
          "description": "Real-time crypto whale tracking platform. Track whale trades, liquidations, and large positions across major exchanges.",
          "applicationCategory": "FinanceApplication",
          "operatingSystem": "Web",
          "offers": {
            "@type": "Offer",
            "price": "0",
            "priceCurrency": "USD"
          },
          "author": {
            "@type": "Organization",
            "name": "TraderMap",
            "url": BASE_URL
          }
        };
      }

      jsonLdScript.textContent = JSON.stringify(jsonLd);
    }
    // Note: For coin pages (coinDetails), JSON-LD is injected server-side
    // with real price data to ensure Google sees it on first crawl

    // Organization JSON-LD — injected on every page
    let orgScript = document.querySelector('script[data-schema="organization"]');
    if (!orgScript) {
      orgScript = document.createElement('script');
      orgScript.setAttribute('type', 'application/ld+json');
      orgScript.setAttribute('data-schema', 'organization');
      document.head.appendChild(orgScript);
    }
    orgScript.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "TraderMap",
      "url": "https://tradermap.io",
      "logo": "https://tradermap.io/assets_landing/img/trace.svg",
      "sameAs": [
        "https://x.com/tradermap_io",
        "https://t.me/tradermap_io",
        "https://www.youtube.com/@Tradermap_io",
        "https://instagram.com/tradermap_io"
      ],
      "description": "Free real-time crypto market intelligence platform. Track whale trades, liquidations, and large positions across major exchanges."
    });

    // Cleanup function
    return () => {
      // Remove hreflang tags on unmount (they will be re-added by next page)
      SUPPORTED_LANGUAGES.forEach(lang => {
        const link = document.querySelector(`link[rel="alternate"][hreflang="${lang}"]`);
        if (link) link.remove();
      });
      const xDefaultLink = document.querySelector('link[rel="alternate"][hreflang="x-default"]');
      if (xDefaultLink) xDefaultLink.remove();

      const orgScript = document.querySelector('script[data-schema="organization"]');
      if (orgScript) orgScript.remove();
    };
  }, [page, language, t, noindex, dynamicParams, skipTitle, coinData, customTitle, customDescription, customImage, keywords, articleData]);

  return null; // This component doesn't render anything
}
